import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import LabLockGate from '../components/LabLockGate';
import { PlaceholderImage } from '../components/PlaceholderMedia';
import labsData from '../data/labs.json';
import { trackEvent } from '../analytics';

const CornerBadge = ({ label }) => (
  <div className="card-corner-bottom-right">
    <div className="text-meta">{label}</div>
    <div className="corner-bottom-right top w-embed">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
      </svg>
    </div>
    <div className="corner-bottom-right bottom w-embed">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
      </svg>
    </div>
  </div>
);

// Blank lines separate blocks. A block whose lines are "1. …", "2. …" (optionally
// under a one-line heading) becomes a numbered list; anything else stays a paragraph.
const LabText = ({ text }) => (
  <div className="lab-text">
    {text.split(/\n\s*\n/).map((block, i) => {
      const lines = block.split('\n');
      const start = /^\d+\.\s/.test(lines[0]) ? 0 : 1;
      const items = lines.slice(start);
      if (items.length > 1 && items.every((line) => /^\d+\.\s/.test(line))) {
        return (
          <div key={i}>
            {start ? <p className="lab-steps-heading">{lines[0]}</p> : null}
            <ol className="lab-steps">
              {items.map((line) => (
                <li key={line}>{line.replace(/^\d+\.\s+/, '')}</li>
              ))}
            </ol>
          </div>
        );
      }
      return (
        <p key={i} style={{ whiteSpace: 'pre-line' }}>
          {block}
        </p>
      );
    })}
  </div>
);

const LabDetail = () => {
  const { slug } = useParams();
  const labIndex = labsData.findIndex((item) => item.slug === slug);
  const lab = labIndex >= 0 ? labsData[labIndex] : null;
  const nextLab = lab && labsData.length > 1 ? labsData[(labIndex + 1) % labsData.length] : null;

  const [unlocked, setUnlocked] = useState(
    () => !lab?.passwordHash || sessionStorage.getItem(`lab-unlocked:${slug}`) === '1'
  );
  const iframeRef = useRef(null);
  const labLinks = lab?.links || (lab?.url ? [{ label: 'Link', url: lab.url }] : []);

  if (!lab) {
    return (
      <main className="main-wrapper">
        <div className="main-wrapper-left"></div>
        <div className="main-wrapper-right">
          <section className="project-about">
            <div className="project-about-left">
              <h1 className="heading-style-h1">Experiment not found</h1>
              <div className="max-width-medium">
                <div>We couldn’t find that experiment. Please return to the labs list.</div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </main>
    );
  }

  // Same-origin iframe: track the first real interaction with the experiment
  const handleIframeLoad = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    const once = () => {
      win.removeEventListener('pointerdown', once);
      trackEvent('Labs', 'play', lab.slug);
    };
    win.addEventListener('pointerdown', once);
  };

  return (
    <main className="main-wrapper">
      <div className="main-wrapper-left">
        <div className="project-hero-image-wrapper lab-hero">
          <CornerBadge label={lab.category} />
          {unlocked ? (
            <iframe
              ref={iframeRef}
              className="lab-embed"
              src={lab.embed.src}
              title={lab.embed.title || lab.title}
              onLoad={handleIframeLoad}
            />
          ) : (
            <LabLockGate slug={lab.slug} hash={lab.passwordHash} onUnlock={() => setUnlocked(true)} />
          )}
        </div>
      </div>
      <div className="main-wrapper-right">
        <section className="project-about">
          <div className="project-about-left">
            <h1 className="heading-style-h1">{lab.title}</h1>
            {unlocked ? (
              <>
                <div className="max-width-medium">
                  <LabText text={lab.excerpt} />
                </div>
                {lab.credits?.length ? (
                  <div className="max-width-medium project-credits">
                    {lab.credits.map((credit) => (
                      <div key={credit.role}>
                        <strong>{credit.role}:</strong> {credit.name}
                      </div>
                    ))}
                  </div>
                ) : null}
                {labLinks.length ? (
                  <div className="card-corner-bottom-right">
                    <div className="text-meta">
                      {labLinks.map((link, i) => (
                        <React.Fragment key={link.url}>
                          {i ? ' · ' : null}
                          <a href={link.url} target="_blank" rel="noreferrer">
                            {link.label}
                          </a>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="corner-bottom-right top w-embed">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
                      </svg>
                    </div>
                    <div className="corner-bottom-right bottom w-embed">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
                      </svg>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="max-width-medium">
                <div className="text-color-muted">This experiment is password-protected.</div>
              </div>
            )}
          </div>
          <div className="project-about-right">
            <div className="project-about-card">
              <div className="text-meta text-color-muted">Year</div>
              <div className="text-meta">{lab.year}</div>
            </div>
            <div className="project-about-card">
              <div className="text-meta text-color-muted">Category</div>
              <div className="text-meta">{lab.category}</div>
            </div>
            <div className="project-about-card">
              <div className="text-meta text-color-muted">Status</div>
              <div className="text-meta">{lab.status}</div>
            </div>
            <div className="project-about-spacer"></div>
          </div>
        </section>

        {unlocked && lab.media?.length ? (
          <section className="lab-media">
            {lab.media.map((item) => (
              <figure key={item.src} className="lab-media-item">
                <div className="lab-media-frame">
                  {item.type === 'video' ? (
                    <video
                      src={item.src}
                      poster={item.poster}
                      controls
                      playsInline
                      preload="metadata"
                      onPlay={() => trackEvent('Labs', 'video_play', lab.slug)}
                    />
                  ) : (
                    <PlaceholderImage src={item.src} alt={item.alt || ''} loading="lazy" />
                  )}
                </div>
                {item.caption ? (
                  <figcaption className="text-meta text-color-muted">{item.caption}</figcaption>
                ) : null}
              </figure>
            ))}
          </section>
        ) : null}

        {unlocked && nextLab ? (
          <section className="project-next-section">
            <Link to={`/labs/${nextLab.slug}`} className="project-next-card w-inline-block">
              <div className="project-next-card-content">
                <div className="label">
                  <div className="label-text">Next Experiment</div>
                </div>
                <div className="project-next-card-info">
                  <h2 className="heading-style-h2">{nextLab.title}</h2>
                  <div className="text-meta text-color-muted">
                    {nextLab.category} · {nextLab.year}
                  </div>
                </div>
                <div className="button-text">
                  <div className="button-text-inner">
                    <div className="button-text-text">View Experiment</div>
                    <div className="project-next-card-arrow w-embed">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M198,64V168a6,6,0,0,1-12,0V78.48L68.24,196.24a6,6,0,0,1-8.48-8.48L177.52,70H88a6,6,0,0,1,0-12H192A6,6,0,0,1,198,64Z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="button-text-line"></div>
                </div>
              </div>
              <div className="project-next-card-media">
                <PlaceholderImage className="project-next-card-image" src={nextLab.poster} alt={nextLab.title} loading="lazy" />
              </div>
            </Link>
          </section>
        ) : null}

        <Footer />
      </div>
    </main>
  );
};

export default LabDetail;
